<template>
  <div id="commissions-by-month">
    <v-card class="px-4 pt-2 pb-4 my-0 elevation-0">
      <v-card-title class="px-1">
        <h1 class="card-title mb-1">
          <template v-if="showDateTypeSelector">
            {{ $t(`views.dashboardDetail.commissionInterest.commissionsByMonth.title`) }}
            <v-chip-group v-model="dateType" active-class="primary" mandatory column class="ml-3">
              <v-chip filter value="month" small class="mx-0">
                {{ $t('common.month') }}
              </v-chip>
              <span class="mx-1">/</span>
              <v-chip filter value="year" small class="mx-0">
                {{ $t('common.year') }}
              </v-chip>
            </v-chip-group>
          </template>
          <template v-else>
            {{ $t(`views.dashboardDetail.commissionInterest.commissionsByMonth.title`) }}
            {{ $t(`common.${dateType}`) }}
          </template>
        </h1>
        <v-spacer />
      </v-card-title>
      <v-card-text class="mt-0 pa-0">
        <BarLoading v-if="!showTable && !loadedData" />
        <BarChart
          v-if="loadedData"
          ref="barChart"
          :chartData="barChartData"
          :chartOptions="barChartOptions"
        />

        <template v-if="showTable">
          <div class="my-5" />
          <v-data-table
            id="commissions-by-month-table"
            :headers="headers"
            :items="mySummary"
            class="elevation-0 left-sticky-table"
            mobile-breakpoint="0"
            :loading="!loadedData"
            :items-per-page="5"
            :page.sync="currentPage"
            :item-class="itemRowBackground"
            :dense="user.preferences.denseTables"
          >
            <template slot="body.prepend">
              <tr v-if="totalRow" class="row-total">
                <td>TOTAL</td>
                <td class="text-right">
                  <currency
                    :value="totalRow.orderTotalCommission"
                    :currency="user.baseCurrency"
                    strong
                  />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.dividendTotalCommission"
                    :currency="user.baseCurrency"
                    strong
                  />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.totalCommission"
                    :currency="user.baseCurrency"
                    strong
                  />
                </td>
                <td class="text-right">
                  <currency
                    :value="totalRow.totalPaidInterest"
                    :currency="user.baseCurrency"
                    strong
                  />
                </td>
              </tr>
            </template>
            <template v-slot:item._id="{ item }">
              <span>
                <template v-if="dateType === 'month'">
                  {{ $t(`common.months.${item._id.month}`) }} {{ item._id.year }}
                </template>
                <template v-else>
                  {{ item._id }}
                </template>
              </span>
            </template>
            <template v-slot:item.orderTotalCommission="{ item }">
              <currency :value="item.orderTotalCommission || 0" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.dividendTotalCommission="{ item }">
              <currency :value="item.dividendTotalCommission || 0" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.totalCommission="{ item }">
              <currency :value="item.totalCommission || 0" :currency="user.baseCurrency" />
            </template>
            <template v-slot:item.totalPaidInterest="{ item }">
              <currency :value="item.totalPaidInterest || 0" :currency="user.baseCurrency" />
            </template>
          </v-data-table>
        </template>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { map } from 'lodash'
import { mapState } from 'vuex'
import { getSummary } from '@/lib/api.js'
import { DARK_DEFAULT_PALETTE, DONUT_PALETTE, DEFAULT_PALETTE } from '@/lib/colors.js'
import { getCurrencyValueAsString, getCurrencySymbol } from '@/lib/utils/currencies.js'
import Currency from '@/views/components/common/Currency'
import BarChart from '@/views/components/core/chartjs/BarChart'
import BarLoading from '@/views/components/core/BarLoading'

export default {
  components: { Currency, BarChart, BarLoading },
  props: {
    showTable: {
      type: Boolean,
      default: true,
    },
    showDateTypeSelector: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    summary: null,
    mySummary: [],
    totalRow: null,
    currentPage: null,
    loadedData: false,
    dateType: 'month',
    barChartData: {
      labels: null,
      datasets: null,
    },
  }),
  computed: {
    ...mapState(['user', 'hideAmounts']),
    barChartOptions() {
      const context = this
      return {
        maintainAspectRatio: false,
        scales: {
          xAxes: [
            {
              gridLines: {
                display: false,
              },
              stacked: true,
            },
          ],
          yAxes: [
            {
              stacked: true,
              ticks: {
                callback: function (value) {
                  return getCurrencyValueAsString({
                    value,
                    currency: context.user.baseCurrency,
                    hide: context.hideAmounts,
                  })
                },
              },
            },
          ],
        },
        tooltips: {
          callbacks: {
            label: function (tooltipItem, data) {
              let label = data.datasets[tooltipItem.datasetIndex].label || ''
              if (label) label += ': '
              label += getCurrencyValueAsString({
                value: tooltipItem.yLabel,
                currency: context.user.baseCurrency,
                hide: context.hideAmounts,
              })
              return label
            },
          },
        },
      }
    },
    headers() {
      return [
        {
          text:
            this.dateType === 'month'
              ? this.$t('views.orders.ordersTable.month')
              : this.$t('views.orders.ordersTable.year'),
          value: '_id',
          sortable: false,
          divider: true,
        },
        {
          text: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.orderTotalCommission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'orderTotalCommission',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.dividendTotalCommission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'dividendTotalCommission',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.totalCommission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalCommission',
          align: 'right',
        },
        {
          text: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.totalPaidInterest'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          value: 'totalPaidInterest',
          align: 'right',
        },
      ]
    },
  },
  watch: {
    dateType: {
      handler() {
        this.changeType()
      },
    },
  },
  methods: {
    itemRowBackground(item) {
      if (!item._id) return 'row-total'
    },
    createBarChartObject() {
      const summary = this.mySummary.slice().reverse()

      this.barChartData.datasets = [
        {
          label: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.orderTotalCommission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          data: map(summary, 'orderTotalCommission'),
          backgroundColor: this.$vuetify.theme.dark
            ? DARK_DEFAULT_PALETTE.chartsBarRed
            : DEFAULT_PALETTE.chartsBarRed,
        },
        {
          label: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.dividendTotalCommission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          data: map(summary, 'dividendTotalCommission'),
          backgroundColor: DONUT_PALETTE.q,
        },
        {
          label: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.totalCommission'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          data: map(summary, 'totalCommission'),
          backgroundColor: DONUT_PALETTE.r,
        },
        {
          label: `${this.$t(
            'views.dashboardDetail.commissionInterest.commissionsByMonth.totalPaidInterest'
          )} (${getCurrencySymbol(this.user.baseCurrency)})`,
          data: map(summary, 'totalPaidInterest'),
          backgroundColor: DEFAULT_PALETTE.darkBackgroundGrey,
        },
      ]

      let labels
      if (this.dateType === 'month') {
        labels = map(
          summary,
          (item) =>
            this.$t(`common.months.${item._id.month}`).substring(0, 3) +
            ` ${item._id.year.toString().slice(-2)}`
        )
      } else {
        labels = map(summary, (item) => item._id)
      }
      this.barChartData.labels = labels

      if (this.$refs.barChart) this.$refs.barChart.render()
    },
    changeType() {
      const summary = JSON.parse(JSON.stringify(this.summary))

      if (summary.length && this.dateType === 'month') {
        let summaryMonths = []
        summary.forEach((year) => {
          if (year.months) summaryMonths = summaryMonths.concat(year.months)
        })
        this.totalRow = summary.shift()
        this.mySummary = summaryMonths
      } else {
        this.totalRow = summary.shift()
        this.mySummary = summary
      }
      this.currentPage = 1

      this.createBarChartObject()
    },
    async loadSummary(filters) {
      this.loadedData = false
      try {
        const summary = await getSummary({
          type: 'commission',
          queryString: {
            group: 'byMonth',
            filter: {
              ...filters,
            },
          },
        })
        this.summary = summary.data

        const isAllDate = !filters.date || Object.keys(filters.date).length === 0
        if (isAllDate) {
          this.$set(this, 'dateType', 'year')
        } else {
          this.$set(this, 'dateType', 'month')
        }

        this.changeType()
      } catch (error) {
        this.$root.$snackbar.checkAndOpenCommonLoadError(error)
      }
      this.loadedData = true
    },
  },
}
</script>

<style lang="scss">
#commissions-by-month {
  .card-title {
    display: flex;
    align-items: center;
  }
}
</style>
